/**
 * Карточка организации в режиме только для чтения (company-profile-and-contacts):
 * реквизиты как текст, без полей ввода, с подсказкой «реквизиты ведёт Центрика».
 * Используется на клиентской стороне (Настройки) — клиент реквизиты не правит.
 */
import { useState, type ReactNode } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ChevronDown } from 'lucide-react'
import type { CompanyProfile } from '@/lib/api'
import { Card, cardVariants } from '@/design-system/components/layout/card'
import { Hint } from '@/design-system/components/primitives/text'
import { cn } from '@/lib/utils'

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0">
      <div className="flex-1 text-txt-secondary">{label}</div>
      <span className="text-right font-medium text-txt">{value || '—'}</span>
    </div>
  )
}

export interface CompanyProfileViewProps {
  profile: CompanyProfile
  /** Текст подсказки (по умолчанию — для раздела «Настройки»). */
  hint?: string
  /** Заголовок карточки (по умолчанию — «Организация»; переопределяется для карточки заказа). */
  title?: string
  /**
   * Сворачиваемая карточка: в заголовке появляется кнопка с шевроном, реквизиты и подсказка
   * показываются только в раскрытом состоянии (centrika-docs!56, page.team-order).
   */
  collapsible?: boolean
  /** Начальное состояние для `collapsible`: по умолчанию карточка открыта. */
  defaultCollapsed?: boolean
}

export function CompanyProfileView({
  profile,
  hint = 'Реквизиты ведёт Центрика. Нужны правки — напишите менеджеру.',
  title = 'Организация',
  collapsible = false,
  defaultCollapsed = false,
}: CompanyProfileViewProps) {
  const [open, setOpen] = useState(!(collapsible && defaultCollapsed))

  const body = (
    <>
      <Row label="Полное наименование" value={profile.nameFull} />
      <Row label="Сокращённое наименование" value={profile.nameShort} />
      <Row label="ИНН" value={profile.inn} />
      <Row label="КПП" value={profile.kpp} />
      <Row label="ОГРН" value={profile.ogrn} />
      <Row label="ОКПО" value={profile.okpo} />
      <Row label="ОКТМО" value={profile.oktmo} />
      <Row label="ОКВЭД" value={profile.okved} />
      <Row label="Юридический адрес" value={profile.legalAddress} />
      <Row label="Email для документов" value={profile.email} />
      <Row label="Руководитель" value={profile.directorName} />
      <Row label="Должность руководителя" value={profile.directorPosition} />
      <Hint className="mt-2.5">{hint}</Hint>
    </>
  )

  if (!collapsible) {
    return <Card header={title}>{body}</Card>
  }

  // Сворачиваемый вариант — на @radix-ui/react-collapsible (тот же паттерн, что у traffic-row):
  // Radix сам ведёт aria-expanded/aria-controls и монтирование Content. Поверхность — те же
  // токены, что у Card (cardVariants), тело показывается только в раскрытом состоянии.
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className={cardVariants({ variant: 'surface' })}>
      <Collapsible.Trigger asChild>
        <button
          type="button"
          className={cn(
            'ds-focus-ring flex w-full items-center justify-between gap-3 px-5 py-4 text-left font-semibold',
            open && 'border-b border-line',
          )}
        >
          <span>{title}</span>
          <ChevronDown
            aria-hidden
            className={cn('size-4 shrink-0 stroke-current transition-transform', open && 'rotate-180')}
          />
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div className="px-5 py-4">{body}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
